import { createApi } from "@reduxjs/toolkit/query/react";
import endpoints, { baseURL } from "data/api/v1/endpoints.ts";
import {
	APIErrorResponse,
	baseQueryWithRedirect,
	encodePostRequest,
	UntransformedResponse
} from "features/api/utils/apiUtils.ts";
import { PostOrdersRequestBody } from "types/api/orders/postOrders";
import {
	ValidateProductsRequestBody,
	ValidateProductsResponse,
	ValidateProductsResponseData
} from "types/api/products/postValidate";
import { v4 as uuidv4 } from "uuid";
import {
	BillTosShipTosRequestBody,
	BillTosShipTosResponse,
	BillTosShipTosResponseData
} from "types/api/accounts/billTosShipTos.ts";
import {
	GetModificationsParameters,
	GetModificationsResponse,
	GetModificationsResponseData
} from "types/api/products/getModifications.ts";
import {
	GetProductsQueryParameters,
	GetProductsResponse,
	GetProductsResponseData
} from "types/api/products/getProducts.ts";
import { PostParseCatalogResponse, PostParseCatalogResponseData } from "types/api/orders/postParseCatalogs.ts";
import { PostParseOrderResponse, PostParseOrderResponseData } from "types/api/orders/postParseOrder.ts";
import {
	GetSpecialModificationsResponse,
	GetSpecialModificationsResponseData
} from "types/api/products/getSpecialModifications.ts";

export type BillToShipToBody = {
	accountId: string;
	billToId: string;
};

export const lineItemIdSymbol = Symbol("lineItemId");
export const specialModIdSymbol = Symbol("specialModId");

export const newOrderApiSlice = createApi({
	reducerPath: "newOrderAPI",
	baseQuery: baseQueryWithRedirect(baseURL),
	endpoints: (builder) => ({
		validateOrderRequest: builder.mutation<ValidateProductsResponseData, ValidateProductsRequestBody>({
			query: (orderRequest: ValidateProductsRequestBody) => ({
				url: endpoints.newOrder.validateOrderRequest(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(orderRequest)
			}),
			extraOptions: {
				handle502Error: true
			},
			transformResponse: (response: ValidateProductsResponse) => {
				if (!response.data) return;
				return {
					...response.data,
					configurations: response.data?.configurations?.map((config) => ({
						...config,
						lineItems: config.lineItems?.map((lineItem) => ({ ...lineItem, [lineItemIdSymbol]: uuidv4() }))
					}))
				};
			},
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		createOrder: builder.mutation({
			query: (order: PostOrdersRequestBody | undefined) => ({
				url: endpoints.newOrder.postNewOrder(),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(order)
			}),
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		newOrderShipToAddresses: builder.mutation<BillTosShipTosResponseData, BillTosShipTosRequestBody>({
			query: (accountIds: BillTosShipTosRequestBody | undefined) => ({
				url: endpoints.newOrder.getBillToShipTosNewOrders(),
				method: "POST",
				dataType: "json",
				body: accountIds
			}),
			transformResponse: (response: BillTosShipTosResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		getProducts: builder.query<GetProductsResponseData, GetProductsQueryParameters>({
			query: (params: GetProductsQueryParameters) =>
				endpoints.newOrder.getProductsByProductLineCode(
					params.productLineId,
					params.productId,
					params.sampleProducts
				),
			transformResponse: (response: GetProductsResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		getModifications: builder.query<GetModificationsResponseData, GetModificationsParameters>({
			query: (args: GetModificationsParameters) => {
				const { productId, productLineId, includeCQComplexityMods } = args;
				return {
					url: endpoints.newOrder.getModifications(productId, productLineId, includeCQComplexityMods)
				};
			},
			transformResponse: (response: GetModificationsResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		getSpecialModifications: builder.query<GetSpecialModificationsResponseData, void>({
			query: () => ({
				url: endpoints.newOrder.getSpecialModifications()
			}),
			transformResponse: (response: GetSpecialModificationsResponse) => {
				return response?.data?.map((mod) => ({ ...mod, [specialModIdSymbol]: uuidv4() }));
			},
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		cancelOrder: builder.mutation({
			query: (orderId: string | null | undefined) => ({
				url: endpoints.newOrder.cancelOrder(orderId),
				method: "POST",
				dataType: "json",
				headers: {
					"Content-type": "application/json; charset=UTF-8"
				},
				body: encodePostRequest(orderId)
			}),
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		parseCatalogs: builder.mutation<PostParseCatalogResponseData, FormData>({
			query: (formData) => ({
				url: endpoints.newOrder.parseCatalogs(),
				method: "POST",
				formData: true,
				body: formData
			}),
			transformResponse: (response: PostParseCatalogResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		}),
		parseOrder: builder.mutation<PostParseOrderResponseData, PostOrdersRequestBody[]>({
			query: (catalogs) => ({
				url: endpoints.newOrder.parseOrder(),
				method: "POST",
				dataType: "json",
				body: encodePostRequest(catalogs)
			}),
			transformResponse: (response: PostParseOrderResponse) => response?.data,
			transformErrorResponse: (error: UntransformedResponse<APIErrorResponse>) => ({
				errors: error?.data?.errors,
				status: error?.status
			})
		})
	})
});

export const {
	useCreateOrderMutation,
	useCancelOrderMutation,
	useValidateOrderRequestMutation,
	useNewOrderShipToAddressesMutation,
	useGetProductsQuery,
	useGetModificationsQuery,
	useGetSpecialModificationsQuery,
	useParseCatalogsMutation,
	useParseOrderMutation
} = newOrderApiSlice;
